import {
  Accordion,
  Container,
  Flex,
  Link,
  LoadingIcon,
  Stack,
  Text,
} from "basis";
import { useAccounts } from "library/accounts";
import { Account } from "library/accounts/types";
import { getRegional } from "library/getRegional";
import { Product } from "library/hooks/types";
import { useConfigFeature } from "library/hooks/useConfig";
import { useProducts } from "library/hooks/useProducts";
import { useEffect } from "react";
import { useEventTracking, usePageTracking } from "react-event-tracker";
import { useNavigate } from "react-router-dom";
import { Layout } from "../Layout/Layout";
import { RelayService } from "../RelayService/RelayService";
import { Zendesk } from "../Zendesk/Zendesk";
import { ContactSection } from "./ContactSection/ContactSection";

interface Params {
  accounts?: {
    credit_cards: Account[];
    personal_loans: Account[];
  };
  products: Product[];
}
function filterOnlyUsersProducts({ accounts, products }: Params): Product[] {
  const userProductIds = {};

  (accounts?.credit_cards || []).filter(Boolean).forEach((creditCard) => {
    userProductIds[creditCard.product_id] = true;
  });

  (accounts?.personal_loans || []).filter(Boolean).forEach((personalLoan) => {
    userProductIds[personalLoan.product_id] = true;
  });

  return products.filter((product) => userProductIds[product.id] === true);
}

const NeedToFindAnAnswer = () => (
  <Container bg="secondary.lightBlue.t25" padding="4">
    <Container bg="white" padding="6">
      <Stack gap="4">
        <Text as="h4" textStyle="heading5">
          Need to find an answer?
        </Text>
        <Link href="/faq" newTab={false}>
          Check out our FAQs
        </Link>
      </Stack>
    </Container>
  </Container>
);

const Complaints = () => {
  const { trackEvent, getQueryString } = useEventTracking();
  return (
    <Container bg="secondary.lightBlue.t25" padding="4">
      <Container bg="white" padding="6">
        <Stack gap="4">
          <Text as="h4" textStyle="heading5">
            Complaints
          </Text>
          <Text>
            We know sometimes things don't go as well as they should and
            problems arise. If this happens to you please let us know so we can
            fix the problem.
          </Text>
          <Link
            href={`${getRegional("complaints")}/?${getQueryString({
              event: {
                category: "text-link",
                action: "internal-link",
                location: "contact-us",
                label: "Complaints information",
              },
            })}`}
            newTab
            onClick={() =>
              trackEvent({
                event: {
                  category: "text-link",
                  action: "internal-link",
                  location: "contact-us",
                  label: "Complaints information",
                },
              })
            }
          >
            Complaints information
          </Link>
        </Stack>
      </Container>
    </Container>
  );
};

export const Contact = () => {
  const navigate = useNavigate();
  const accountsInfo = useAccounts({
    fullCreditCardsInfo: true,
    fullPersonalLoansInfo: true,
  });

  usePageTracking({
    page: {
      pageType: "information-page",
      siteSection: "contact",
      siteSubSection: "",
      pageTitle: "Contact us",
    },
    user: {
      authenticationStatus: "logged in",
    },
  });

  const productsInfo = useProducts();
  const products = productsInfo.data ?? [];
  const userProducts =
    accountsInfo.isLoading || productsInfo.isLoading
      ? []
      : filterOnlyUsersProducts({ accounts: accountsInfo.data, products });

  const showLiveChatNZ = useConfigFeature("liveChatNZ");
  const showLiveChatAU = useConfigFeature("liveChatAU");

  useEffect(() => {
    if (accountsInfo.error?.response?.status === 401) {
      navigate("/login");
    }
  }, [accountsInfo, navigate]);

  return (
    <Layout>
      <Layout.Header>Contact us</Layout.Header>
      <Layout.Main>
        {(showLiveChatAU || showLiveChatNZ) && <Zendesk />}

        {accountsInfo.isLoading ? (
          <Flex height="200" placeItems="center">
            <LoadingIcon size="medium" />
          </Flex>
        ) : (
          <Accordion itemHeaderAs="h2">
            {userProducts.map((product) => (
              <Accordion.Item
                initiallyOpen={userProducts.length === 1}
                key={product.id}
              >
                <Accordion.Item.Header>{product.name}</Accordion.Item.Header>
                <Accordion.Item.Content>
                  <ContactSection product={product} />
                </Accordion.Item.Content>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </Layout.Main>
      <Layout.Sidebar>
        <Stack gap="8">
          <NeedToFindAnAnswer />
          <RelayService />
          <Complaints />
        </Stack>
      </Layout.Sidebar>
    </Layout>
  );
};
